import { useMemo } from "react";
import { queries } from "../../../shared/queries";
import { useQuery } from "../../hooks/useQuery";
import {
  type DisplayableSpace,
  useSpaceDisplayName,
} from "../../hooks/useSpaceDisplayName";
import { makeUrl, routes } from "../../lib/urls";
import { DonutChart } from "../DonutChart";
import { type StatusSegment } from "../StatusBreakdownChart";
import { Text } from "../Text";
import { InfoCorner, PanelBadge } from "./PanelInfo";
import { spaceColorMap } from "./monitoringPanelLayout";
import { useMonitorSpaceWorkflows } from "./useMonitorSpaceWorkflows";
import { Panel, PanelHeader, PanelSkeleton, TotalRow } from "./workflowsLayout";

const THIRTY_DAYS_MS = 30 * 86_400_000;
const PERSONAL_SEGMENT_KEY = "personal";

type SpaceRow = {
  space: DisplayableSpace;
  label: string;
  count: number;
  workflowNames: string[];
};

function isActiveWorkflow(
  workflow: {
    branches?: {
      headCommitId: string | null;
      lastRunAt: number | null;
    } | null;
  },
  since: number
): boolean {
  const main = workflow.branches;
  if (!main?.headCommitId) return false;
  return (main.lastRunAt ?? 0) >= since;
}

export function ActiveWorkflowsBySpace() {
  const since = useMemo(() => Date.now() - THIRTY_DAYS_MS, []);
  const { workflows: monitorWorkflows, result: workflowsQuery } =
    useMonitorSpaceWorkflows();
  const [spaces, spacesQuery] = useQuery(
    queries.spacesLite({ includeAdminSpaces: true })
  );
  const displayName = useSpaceDisplayName();

  const workflows = useMemo(
    () => monitorWorkflows.filter((w) => isActiveWorkflow(w, since)),
    [monitorWorkflows, since]
  );

  const { rows, personalNames } = useMemo(() => {
    const bySpace = new Map<string, string[]>();
    for (const workflow of workflows) {
      const names = bySpace.get(workflow.spaceId) ?? [];
      names.push(workflow.name);
      bySpace.set(workflow.spaceId, names);
    }

    const result: SpaceRow[] = [];
    const personal: string[] = [];
    for (const space of spaces ?? []) {
      const workflowNames = bySpace.get(space.id) ?? [];
      if (space.mode === "personal") {
        personal.push(...workflowNames);
        continue;
      }
      result.push({
        space,
        label: displayName(space),
        count: workflowNames.length,
        workflowNames,
      });
    }

    result.sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
    return { rows: result, personalNames: personal };
  }, [workflows, spaces, displayName]);

  const colorMap = useMemo(() => spaceColorMap(spaces ?? []), [spaces]);

  const segments = useMemo<StatusSegment[]>(() => {
    const spaceSegments = rows
      .filter((row) => row.count > 0)
      .map((row) => ({
        key: row.space.id,
        count: row.count,
        color: colorMap.get(row.space.id)!,
        label: row.label,
        items: row.workflowNames,
      }));
    if (personalNames.length > 0) {
      spaceSegments.push({
        key: PERSONAL_SEGMENT_KEY,
        count: personalNames.length,
        color: "var(--gray-600)",
        label: "Personal",
        items: personalNames,
      });
    }
    return spaceSegments;
  }, [rows, personalNames, colorMap]);

  const total = workflows.length;

  if (workflowsQuery.type !== "complete" || spacesQuery.type !== "complete") {
    return <PanelSkeleton />;
  }

  return (
    <Panel>
      <InfoCorner>
        <PanelBadge>Last 30 days</PanelBadge>
      </InfoCorner>
      <PanelHeader>
        <Text variant="text-small-md">Active workflows by space</Text>
        <TotalRow>
          <Text variant="display-md">{total}</Text>
        </TotalRow>
      </PanelHeader>
      <DonutChart
        segments={segments}
        total={total}
        placeholder={total === 0}
        strokeWidth={6}
        centerCaption="workflows"
        scrollableLegend
        segmentHref={(s) =>
          s.key === PERSONAL_SEGMENT_KEY
            ? undefined
            : makeUrl(routes.space, { spaceId: s.key })
        }
      />
    </Panel>
  );
}
